import {
  PropsWithChildren,
  ReactElement,
  createContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { useWindowFocusRefresh } from '@spotify/confidence-core-react';
import {
  getError,
  getTypeOrNull,
  useWorkflowCreditAvailabilityQuery,
} from '@spotify/confidence-graphql';

type WorkflowCreditAvailability = {
  blocked: boolean;
  refresh: () => Promise<void>;
};

export const WorkflowCreditAvailabilityContext = createContext<
  WorkflowCreditAvailability | undefined
>(undefined);

export const WorkflowCreditAvailabilityProvider = ({
  children,
}: PropsWithChildren): ReactElement => {
  const { data, error, refetch } = useWorkflowCreditAvailabilityQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const availability =
    error || getError(data?.queryWorkflowCreditAvailability)
      ? undefined
      : getTypeOrNull(data?.queryWorkflowCreditAvailability);
  const blocked = availability?.transitionsBlocked === true;
  const refresh = useCallback(async () => {
    try {
      await refetch();
    } catch {
      return;
    }
  }, [refetch]);
  const { activate, deactivate } = useWindowFocusRefresh(refresh);
  useEffect(() => {
    activate();
    return deactivate;
  }, [activate, deactivate]);
  const value = useMemo(() => ({ blocked, refresh }), [blocked, refresh]);

  return (
    <WorkflowCreditAvailabilityContext.Provider value={value}>
      {children}
    </WorkflowCreditAvailabilityContext.Provider>
  );
};
